<script setup>
import { computed } from 'vue'
import DataTable from '../Atoms/DataTable.vue'
import TeamMemberCell from './TeamMemberCell.vue'
import TeamRoleCell from './TeamRoleCell.vue'
import TeamAccessCell from './TeamAccessCell.vue'
import TeamProjectsCell from './TeamProjectsCell.vue'
import TeamStatusCell from './TeamStatusCell.vue'
import TeamRowActions from './TeamRowActions.vue'
import TeamMemberCard from './TeamMemberCard.vue'
import { teamRoleKey } from '@/Utils/teamAccessSummary'

const props = defineProps({
	tableData: { type: Array, default: () => [] },
	permissionTogglestatus: { type: Boolean, default: false },
	// activeTeam present → viewing as a member; row mutations gated off backend-side.
	activeTeam: { type: [String, Object, null], default: null },
	// The workspace the page is standing in. Names the per-project actions.
	activeProject: { type: Object, default: null },
	// Whether the account has more than one workspace. Below that the Workspaces
	// column is one chip repeated on every row, so it is left out.
	showProjects: { type: Boolean, default: false },
})

const emit = defineEmits(['edit-member', 'add-member', 'resend-member', 'change-member-status', 'remove-member'])

const data = computed(() => props.tableData || [])
const canMutate = computed(() => !props.activeTeam)
const isOwner = (member) => teamRoleKey(member?.role) === 'owner'
// Paused rows dim once (member + access); the status word + kebab stay full.
// A person not on this workspace dims the same way: they are here for context.
const isDimmed = (member) => (
	member?.onProject === false || (member?.status !== 'pending' && member?.enabled === false)
)

// ROLE gets its own narrow fixed-width column so badges line up vertically
// instead of zig-zagging after variable-length names. Fixed widths (via
// `table-fixed`) keep ROLE / ACCESS / PROJECTS / STATUS / actions aligned;
// MEMBER takes the remaining width and truncates long names.
const fields = computed(() => {
	const cols = [
		{ key: 'name', label: 'Member', thClass: 'text-left', tdClass: 'py-3.5 pr-3 align-middle' },
		{ key: 'role', label: 'Role', thClass: 'text-left w-28', tdClass: 'py-3.5 pr-3 w-28 align-middle' },
		{ key: 'access', label: 'Access', thClass: 'text-left w-40', tdClass: 'py-3.5 pr-3 w-40 align-middle' },
	]
	if (props.showProjects) {
		cols.push({ key: 'projects', label: 'Workspaces', thClass: 'text-left w-48', tdClass: 'py-3.5 pr-3 w-48 align-middle' })
	}
	cols.push(
		{ key: 'status', label: 'Status', thClass: 'text-left w-32', tdClass: 'py-3.5 pr-3 w-32 align-middle' },
		{ key: 'actions', label: '', thClass: 'text-right w-12', tdClass: 'py-3.5 text-right w-12 align-middle' },
	)
	return cols
})

const forward = (event, payload) => emit(event, payload)
</script>

<template>
	<!-- Mobile / tablet / laptop: cards with faint row separation (no hard lines) -->
	<div class="divide-y divide-white/[0.06] lg:hidden">
		<TeamMemberCard
			v-for="member in data"
			:key="member._id || member.id || member.email"
			:member="member"
			:permission-togglestatus="permissionTogglestatus"
			:can-mutate="canMutate"
			:active-project="activeProject"
			:show-projects="showProjects"
			@edit="forward('edit-member', $event)"
			@add="forward('add-member', $event)"
			@resend="forward('resend-member', $event)"
			@toggle="forward('change-member-status', $event)"
			@remove="forward('remove-member', $event)"
		/>
		<p v-if="!data.length" class="text-sm text-surface-7 text-center py-6 mb-0">
			No members match this filter
		</p>
	</div>

	<!-- Desktop: table -->
	<div class="hidden lg:block px-4 pb-2">
		<DataTable
			:fields="fields"
			:items="data"
			primary-key="_id"
			table-class="w-full table-fixed text-sm [&_tbody]:divide-y [&_tbody]:divide-white/[0.04] [&_tbody_tr]:transition-colors [&_tbody_tr:hover]:bg-surface-3/50"
			thead-class="text-tiny uppercase tracking-wider text-surface-6 [&_th]:font-medium [&_th]:py-2.5 [&_th]:border-b [&_th]:border-white/[0.06]"
		>
			<template #cell(name)="{ item }">
				<TeamMemberCell :member="item" :dim="isDimmed(item)" :inline-role="false" />
			</template>
			<template #cell(role)="{ item }">
				<TeamRoleCell :member="item" :class="isDimmed(item) ? 'opacity-60' : ''" />
			</template>
			<template #cell(access)="{ item }">
				<TeamAccessCell :member="item" :class="isDimmed(item) ? 'opacity-60' : ''" />
			</template>
			<template #cell(projects)="{ item }">
				<TeamProjectsCell :member="item" :active-project-id="activeProject?.id || ''" />
			</template>
			<template #cell(status)="{ item }">
				<TeamStatusCell :member="item" />
			</template>
			<template #cell(actions)="{ item }">
				<TeamRowActions
					v-if="!isOwner(item)"
					:member="item"
					:permission-togglestatus="permissionTogglestatus"
					:can-mutate="canMutate"
					:active-project="activeProject"
					:show-projects="showProjects"
					@edit="forward('edit-member', $event)"
					@add="forward('add-member', $event)"
					@resend="forward('resend-member', $event)"
					@toggle="forward('change-member-status', $event)"
					@remove="forward('remove-member', $event)"
				/>
			</template>
		</DataTable>
		<p v-if="!data.length" class="text-sm text-surface-7 text-center py-6 mb-0">
			No members match this filter
		</p>
	</div>
</template>
